import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Calendar, Building2, QrCode, Trash2, Copy, Users, ArrowDownLeft, ArrowUpRight, UserPlus, Share2, X, Download, Printer, Pencil, FileText, Minus } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { API } from '../../utils/api';
import { GuestDetailsSwitch, OptionalDetailsBadge } from '../../components/GuestDetails';
import { useAuthContext } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { downloadFlyer as downloadFlyerHtml } from '../../utils/flyer';

export function BusinessSubAccounts({ initialSubAccountId, onNavigateToHistory }) {
  const { t } = useTranslation();
  const { headers, user } = useAuthContext();
  const [subAccounts, setSubAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', default_message: '', start_date: '', end_date: '', swish_account_id: '', accounting_code: '', fixed_amount: '', payment_mode: 'AMOUNT', line_items: [], require_guest_details: true });
  const [form, setForm] = useState({ name: '', description: '', type: 'EVENT', start_date: '', end_date: '', default_message: '', swish_account_id: '', accounting_code: '', fixed_amount: '', payment_mode: 'AMOUNT', line_items: [], require_guest_details: true });
  const [activeType, setActiveType] = useState('all');
  const [qrOpen, setQrOpen] = useState(null);
  const [flyerOpen, setFlyerOpen] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(null);
  const [inviteSwish, setInviteSwish] = useState('');
  const [expandedId, setExpandedId] = useState(initialSubAccountId || null);
  const [invitations, setInvitations] = useState({});
  const [businessProfile, setBusinessProfile] = useState(null);
  const [swishAccounts, setSwishAccounts] = useState([]);
  const [reportOpen, setReportOpen] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');
  const flyerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/business/profile`, { headers }).then(r => setBusinessProfile(r.data)).catch(() => {});
    axios.get(`${API}/business/swish-accounts`, { headers }).then(r => setSwishAccounts(r.data.items || [])).catch(() => {});
  }, [headers]);

  const fetchSubAccounts = async () => {
    try {
      let params = '?status=';  // Empty status = show all (ACTIVE + DISABLED)
      if (activeType !== 'all') params += `&type=${activeType}`;
      const res = await axios.get(`${API}/business/sub-accounts${params}`, { headers });
      setSubAccounts(res.data.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSubAccounts(); }, [headers, activeType]);
  useEffect(() => { if (initialSubAccountId) setExpandedId(initialSubAccountId); }, [initialSubAccountId]);

  const handleCreate = async () => {
    if (!form.name.trim()) return toast.error(t('business.nameIsRequired'));
    try {
      const payload = { ...form };
      if (!payload.swish_account_id) delete payload.swish_account_id;
      // Handle payment mode
      if (payload.payment_mode === 'AMOUNT') {
        delete payload.line_items;
        if (payload.fixed_amount) {
          payload.fixed_amount = parseFloat(payload.fixed_amount);
          if (isNaN(payload.fixed_amount) || payload.fixed_amount <= 0) delete payload.fixed_amount;
        } else {
          delete payload.fixed_amount;
        }
      } else if (payload.payment_mode === 'LINE_ITEMS') {
        delete payload.fixed_amount;
        if (!payload.line_items || payload.line_items.length === 0) {
          return toast.error('Add at least one item');
        }
        // Validate line items
        for (const li of payload.line_items) {
          if (!li.description.trim() || !li.amount || li.amount <= 0) {
            return toast.error('Each item needs a description and amount');
          }
        }
      }
      await axios.post(`${API}/business/sub-accounts`, payload, { headers });
      toast.success(t('business.subAccountCreated'));
      setCreateOpen(false);
      setForm({ name: '', description: '', type: 'EVENT', start_date: '', end_date: '', default_message: '', swish_account_id: '', accounting_code: '', fixed_amount: '', payment_mode: 'AMOUNT', line_items: [], require_guest_details: true });
      fetchSubAccounts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  const handleDelete = async (id, name) => {
    try {
      const res = await axios.delete(`${API}/business/sub-accounts/${id}`, { headers });
      toast.success(res.data.message || `"${name}" deleted`);
      setDeleteConfirm(null);
      fetchSubAccounts();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleInvite = async (subId) => {
    if (!inviteSwish.trim()) return toast.error(t('business.swishIdRequired'));
    try {
      const res = await axios.post(`${API}/business/sub-accounts/${subId}/invite`, { swish_id: inviteSwish }, { headers });
      toast.success(t('business.inviteSent', { name: res.data.user_name }));
      setInviteOpen(null);
      setInviteSwish('');
      fetchSubAccounts();
      fetchInvitations(subId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to invite'); }
  };

  const fetchInvitations = async (subId) => {
    try {
      const res = await axios.get(`${API}/business/sub-accounts/${subId}/invitations`, { headers });
      setInvitations(prev => ({ ...prev, [subId]: res.data.items || [] }));
    } catch (e) { console.error(e); }
  };

  const revokeInvitation = async (invId, subId) => {
    try {
      await axios.delete(`${API}/business/invitations/${invId}`, { headers });
      toast.success(t('business.invitationRevoked'));
      fetchInvitations(subId);
      fetchSubAccounts();
    } catch (e) { toast.error('Failed to revoke'); }
  };

  const toggleExpand = (id) => {
    if (expandedId === id) { setExpandedId(null); }
    else { setExpandedId(id); fetchInvitations(id); }
  };

  const copyPaymigId = (id) => { navigator.clipboard.writeText(id); toast.success(t('business.copyPaymigId')); };

  const fetchReport = async (sa) => {
    setReportOpen(sa);
    setReportDateFrom(sa.created_at ? sa.created_at.split('T')[0] : '');
    setReportDateTo(sa.end_date ? sa.end_date.split('T')[0] : new Date().toISOString().split('T')[0]);
    setReportData(null);
  };

  const loadReport = async () => {
    if (!reportOpen) return;
    setReportLoading(true);
    try {
      let url = `${API}/business/sub-accounts/${reportOpen.id}/report`;
      const params = [];
      if (reportDateFrom) params.push(`date_from=${reportDateFrom}T00:00:00`);
      if (reportDateTo) params.push(`date_to=${reportDateTo}T23:59:59`);
      if (params.length) url += '?' + params.join('&');
      const res = await axios.get(url, { headers });
      setReportData(res.data);
    } catch (e) { toast.error('Failed to load report'); }
    finally { setReportLoading(false); }
  };

  const downloadReportPdf = () => {
    if (!reportData) return;
    const sa = reportData.sub_account;
    const rows = reportData.rows || [];
    const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    // Collect all unique line item descriptions across all rows
    const allItemDescs = new Set();
    rows.forEach(r => {
      if (r.line_items_breakdown) r.line_items_breakdown.forEach(li => allItemDescs.add(li.description));
    });
    const itemCols = [...allItemDescs];

    const fmtDate = (iso) => {
      if (!iso) return '';
      try { return new Date(iso).toLocaleDateString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }); }
      catch { return iso; }
    };

    let tableRows = rows.map(r => {
      let itemCells = '';
      if (itemCols.length > 0) {
        itemCells = itemCols.map(desc => {
          const li = (r.line_items_breakdown || []).find(x => x.description === desc);
          return `<td style="padding:6px 8px;border:1px solid #e5e7eb;text-align:center;font-size:11px">${li ? li.quantity : ''}</td>`;
        }).join('');
      }
      const guestLabel = r.is_guest && r.guest_name ? `<br><span style="color:#7c3aed;font-size:10px">Guest: ${esc(r.guest_name)}</span>` : '';
      return `<tr>
        <td style="padding:6px 8px;border:1px solid #e5e7eb;font-size:11px;white-space:nowrap">${esc(fmtDate(r.date))}</td>
        <td style="padding:6px 8px;border:1px solid #e5e7eb;font-size:11px">${esc(r.from_name)}${guestLabel}</td>
        <td style="padding:6px 8px;border:1px solid #e5e7eb;font-size:11px">${esc(r.description)}</td>
        <td style="padding:6px 8px;border:1px solid #e5e7eb;font-size:11px">${esc(r.sub_account_name)}</td>
        <td style="padding:6px 8px;border:1px solid #e5e7eb;font-size:11px;text-align:right;font-weight:600">${esc(r.amount)} SEK</td>
        <td style="padding:6px 8px;border:1px solid #e5e7eb;font-size:11px">${esc(r.type)}</td>
        ${itemCells}
      </tr>`;
    }).join('');

    const itemHeaders = itemCols.map(d => `<th style="padding:6px 8px;border:1px solid #e5e7eb;background:#f9fafb;font-size:10px;text-align:center">${esc(d)}<br>(qty)</th>`).join('');

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Report - ${esc(sa.name)}</title>
<style>@page{size:A4 landscape;margin:15mm}body{font-family:'Segoe UI',system-ui,sans-serif;color:#1a1a2e;font-size:12px}
table{border-collapse:collapse;width:100%}th{background:#f3f4f6;font-weight:600;text-align:left}
.header{display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;padding-bottom:12px;border-bottom:2px solid #0d9488}
.title{font-size:20px;font-weight:700;color:#0d9488}.meta{font-size:11px;color:#6b7280}
.summary{margin:12px 0;padding:12px;background:#f0fdfa;border-radius:8px;display:flex;gap:24px}
.summary-item{font-size:13px}.summary-item strong{color:#0d9488}
@media print{.no-print{display:none}}</style></head><body>
<div class="header"><div><div class="title">PayMig Report — ${esc(sa.name)}</div>
<div class="meta">${esc(sa.type)} | PayMig ID: ${esc(sa.display_paymig_id || sa.paymig_id)}</div></div>
<div class="meta">${reportDateFrom || ''} — ${reportDateTo || ''}</div></div>
<div class="summary"><div class="summary-item"><strong>${reportData.transaction_count}</strong> transactions</div>
<div class="summary-item">Total: <strong>${reportData.total_amount} SEK</strong></div></div>
<table><thead><tr>
<th style="padding:6px 8px;border:1px solid #e5e7eb;background:#f3f4f6">Date</th>
<th style="padding:6px 8px;border:1px solid #e5e7eb;background:#f3f4f6">Name</th>
<th style="padding:6px 8px;border:1px solid #e5e7eb;background:#f3f4f6">Swish Message</th>
<th style="padding:6px 8px;border:1px solid #e5e7eb;background:#f3f4f6">Sub Account</th>
<th style="padding:6px 8px;border:1px solid #e5e7eb;background:#f3f4f6;text-align:right">Amount</th>
<th style="padding:6px 8px;border:1px solid #e5e7eb;background:#f3f4f6">Type</th>
${itemHeaders}
</tr></thead><tbody>${tableRows}</tbody></table>
<div style="margin-top:16px;text-align:center;font-size:10px;color:#9ca3af">Generated by PayMig — ${new Date().toISOString().split('T')[0]}</div>
</body></html>`;

    // Render the (fully esc()-escaped) report in a sandboxed same-origin blob
    // document and print it — avoids document.write / innerHTML injection.
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWin = window.open(url, '_blank');
    if (printWin) {
      printWin.addEventListener('load', () => {
        printWin.print();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      });
    } else {
      URL.revokeObjectURL(url);
      toast.error('Please allow pop-ups to download the report.');
    }
  };

  const openEdit = (sa) => {
    setEditForm({
      name: sa.name,
      description: sa.description || '',
      default_message: sa.default_message || '',
      start_date: sa.start_date || '',
      end_date: sa.end_date || '',
      swish_account_id: sa.swish_account_id || '',
      accounting_code: sa.accounting_code || '',
      fixed_amount: sa.fixed_amount || '',
      payment_mode: sa.payment_mode || 'AMOUNT',
      line_items: sa.line_items || [],
      require_guest_details: sa.require_guest_details !== false,
    });
    setEditOpen(sa);
  };

  const handleEdit = async () => {
    if (!editOpen) return;
    try {
      const payload = { ...editForm };
      if (payload.payment_mode === 'AMOUNT') {
        payload.line_items = null;
        if (payload.fixed_amount) {
          payload.fixed_amount = parseFloat(payload.fixed_amount);
          if (isNaN(payload.fixed_amount) || payload.fixed_amount <= 0) payload.fixed_amount = null;
        } else {
          payload.fixed_amount = null;
        }
      } else if (payload.payment_mode === 'LINE_ITEMS') {
        payload.fixed_amount = null;
        if (!payload.line_items || payload.line_items.length === 0) {
          return toast.error('Add at least one item');
        }
      }
      await axios.put(`${API}/business/sub-accounts/${editOpen.id}`, payload, { headers });
      toast.success(t('business.subAccountUpdated'));
      setEditOpen(null);
      fetchSubAccounts();
    } catch (e) { toast.error(e.response?.data?.detail || t('business.failedToUpdate')); }
  };

  const handleToggleStatus = async (sa) => {
    const newStatus = sa.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE';
    try {
      await axios.put(`${API}/business/sub-accounts/${sa.id}`, { status: newStatus }, { headers });
      toast.success(`${sa.name} ${newStatus === 'ACTIVE' ? t('business.enabled') : t('business.disabled')}`);
      fetchSubAccounts();
    } catch (e) { toast.error(t('business.failedToUpdateStatus')); }
  };

  const shareQR = (sa) => {
    const bizName = businessProfile?.company_name || user?.full_name || 'Business';
    const swishId = sa.connected_swish_id || '';
    const text = `PayMig - ${bizName}\n\n${sa.name} (${sa.type})\nPayMig ID: ${sa.display_paymig_id || sa.paymig_id}\nSwish: ${swishId}\n\nhttps://paymig.app`;
    if (navigator.share) {
      navigator.share({ title: `PayMig - ${sa.name}`, text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text);
      toast.success(t('business.paymentInfoCopied'));
    }
  };

  const downloadFlyer = async (sa) => {
    const bizName = businessProfile?.company_name || user?.full_name || 'Business';
    const bizLogo = businessProfile?.logo_path ? `${API}/business/logo-file/${businessProfile.logo_path}` : '';
    await downloadFlyerHtml(sa, {
      bizName,
      bizLogo,
      paymentUrl: sa.payment_url || `${window.location.origin}/pay/${sa.paymig_id}`,
      successMessage: t('business.flyerDownloaded'),
    });
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return iso; }
  };

  const statusColor = (s) => {
    if (s === 'ACCEPTED') return 'bg-emerald-100 text-emerald-700';
    if (s === 'PENDING') return 'bg-yellow-100 text-yellow-700';
    return 'bg-gray-100 text-gray-700';
  };

  if (loading) return <div className="text-center py-8">{t('common.loading')}</div>;

  return (
    <div className="space-y-4" data-testid="business-sub-accounts">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t('business.subAccounts')}</h2>
        <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="create-sub-account-btn">
          <Plus className="h-4 w-4 mr-1" /> {t('common.add')}
        </Button>
      </div>

      <Tabs value={activeType} onValueChange={setActiveType}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="all" data-testid="tab-all">{t('common.all')}</TabsTrigger>
          <TabsTrigger value="EVENT" data-testid="tab-events">{t('business.event')}s</TabsTrigger>
          <TabsTrigger value="DIVISION" data-testid="tab-divisions">{t('business.division')}s</TabsTrigger>
        </TabsList>
      </Tabs>

      {subAccounts.length === 0 ? (
        <Card><CardContent className="text-center py-8 text-muted-foreground" data-testid="no-sub-accounts">{t('business.noSubAccountsYet')}</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {subAccounts.map(sa => (
            <Card key={sa.id} data-testid={`sub-account-card-${sa.id}`} className={`overflow-hidden ${sa.status === 'DISABLED' ? 'opacity-60' : ''}`}>
              <CardContent className="p-3">
                {/* Row 1: Name + type + toggle + actions */}
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-1.5 flex-1 min-w-0">
                    {sa.type === 'EVENT' ? <Calendar className="h-4 w-4 text-blue-500 shrink-0" /> : <Building2 className="h-4 w-4 text-purple-500 shrink-0" />}
                    <span className="font-semibold text-sm truncate">{sa.name}</span>
                    <Badge variant="secondary" className="text-[10px] shrink-0">{sa.type}</Badge>
                    <OptionalDetailsBadge sa={sa} />
                    {sa.status === 'DISABLED' && <Badge variant="destructive" className="text-[10px]">Disabled</Badge>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Switch checked={sa.status === 'ACTIVE'} onCheckedChange={() => handleToggleStatus(sa)} data-testid={`toggle-${sa.id}`} />
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(sa)} data-testid={`edit-${sa.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setQrOpen(sa)} data-testid={`qr-${sa.id}`}><QrCode className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDeleteConfirm(sa)} data-testid={`delete-${sa.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
                  </div>
                </div>

                {/* Row 2: PayMig ID + Swish + accounting code */}
                <div className="flex items-center gap-2 mb-2 text-xs">
                  <span className="font-mono text-muted-foreground">{sa.display_paymig_id || sa.paymig_id}</span>
                  <button onClick={() => copyPaymigId(sa.paymig_id)} className="hover:text-blue-600"><Copy className="h-3 w-3" /></button>
                  {sa.accounting_code && <Badge variant="outline" className="text-[10px] ml-1">{t('auth.accountingCode')}: {sa.accounting_code}</Badge>}
                  {sa.connected_swish_id && <span className="text-muted-foreground ml-auto">Swish: {sa.connected_swish_id}</span>}
                </div>

                {sa.default_message && <p className="text-xs text-blue-700 bg-blue-50 px-2 py-1 rounded mb-2 truncate">Msg: {sa.default_message}</p>}
                {sa.payment_mode === 'LINE_ITEMS' && sa.line_items?.length > 0 ? (
                  <div className="text-xs text-violet-700 bg-violet-50 px-2 py-1 rounded mb-2">
                    {sa.line_items.map((li, i) => <span key={i}>{i > 0 && ' · '}{li.description}: {li.amount} SEK</span>)}
                  </div>
                ) : sa.fixed_amount ? (
                  <p className="text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded mb-2">Fixed: {sa.fixed_amount} SEK</p>
                ) : null}
                {sa.payment_url && <p className="text-xs text-cyan-700 bg-cyan-50 px-2 py-1 rounded mb-2 truncate">Guest Link: {sa.payment_url}</p>}

                {/* Row 3: Stats inline */}
                <div className="flex items-center gap-3 text-xs mb-2">
                  <span className="text-emerald-700"><ArrowDownLeft className="h-3 w-3 inline" /> {(sa.total_received || 0).toLocaleString()} SEK</span>
                  <span className="text-rose-700"><ArrowUpRight className="h-3 w-3 inline" /> {(sa.total_sent || 0).toLocaleString()} SEK</span>
                  <span className="font-semibold text-blue-700">= {(sa.balance ?? 0).toLocaleString()} SEK</span>
                  <span className="text-muted-foreground ml-auto"><Users className="h-3 w-3 inline" /> {sa.moderators?.length || 0}</span>
                  {sa.pending_invitations > 0 && <Badge variant="outline" className="text-[10px] text-orange-600 border-orange-300">{sa.pending_invitations} pending</Badge>}
                </div>

                {/* Row 4: Action buttons */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Button variant="outline" size="sm" className="h-6 text-[11px] gap-1 px-2" onClick={() => setInviteOpen(sa.id)} data-testid={`invite-${sa.id}`}>
                    <UserPlus className="h-3 w-3" /> {t('business.inviteModerator').split(' ')[0]}
                  </Button>
                  <Button variant="outline" size="sm" className="h-6 text-[11px] gap-1 px-2" onClick={() => toggleExpand(sa.id)} data-testid={`expand-${sa.id}`}>
                    {expandedId === sa.id ? t('common.close') : t('business.details')}
                  </Button>
                  <Button variant="outline" size="sm" className="h-6 text-[11px] gap-1 px-2" onClick={() => shareQR(sa)} data-testid={`share-${sa.id}`}>
                    <Share2 className="h-3 w-3" /> {t('business.share')}
                  </Button>
                  <Button variant="outline" size="sm" className="h-6 text-[11px] gap-1 px-2" onClick={() => downloadFlyer(sa)} data-testid={`flyer-${sa.id}`}>
                    <Printer className="h-3 w-3" /> {t('business.downloadFlyer').split(' ')[0]}
                  </Button>
                  <Button variant="outline" size="sm" className="h-6 text-[11px] gap-1 px-2" onClick={() => fetchReport(sa)} data-testid={`report-${sa.id}`}>
                    <FileText className="h-3 w-3" /> {t('business.report')}
                  </Button>
                </div>

                {/* Expanded details */}
                {expandedId === sa.id && (
                  <div className="mt-2 pt-2 border-t space-y-1.5">
                    {sa.description && <p className="text-xs text-muted-foreground">{sa.description}</p>}
                    <div className="flex gap-3 text-xs text-muted-foreground">
                      <span>Created: {formatDate(sa.created_at)}</span>
                      {sa.end_date && <span className="text-orange-600">Expires: {formatDate(sa.end_date)}</span>}
                    </div>
                    <div className="text-xs font-medium mt-1">Moderators</div>
                    {(invitations[sa.id] || []).length === 0 && (sa.moderators || []).length === 0 ? (
                      <p className="text-xs text-muted-foreground">No moderators yet.</p>
                    ) : (
                      <div className="space-y-1">
                        {(invitations[sa.id] || []).map(inv => (
                          <div key={inv.id} className="flex items-center justify-between p-1.5 bg-gray-50 rounded text-xs">
                            <div>
                              <span className="font-medium">{inv.user_name || 'Unknown'}</span>
                              <Badge className={`${statusColor(inv.status)} text-[10px] ml-1`}>{inv.status}</Badge>
                            </div>
                            {inv.status === 'PENDING' && (
                              <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => revokeInvitation(inv.id, sa.id)}><X className="h-3 w-3 text-rose-500" /></Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog - with Swish dropdown + accounting code */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('business.createSubAccount')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex gap-2">
              <Button variant={form.type === 'EVENT' ? 'default' : 'outline'} size="sm" onClick={() => setForm(f => ({ ...f, type: 'EVENT' }))} data-testid="type-event-btn">
                <Calendar className="h-4 w-4 mr-1" /> {t('business.event')}
              </Button>
              <Button variant={form.type === 'DIVISION' ? 'default' : 'outline'} size="sm" onClick={() => setForm(f => ({ ...f, type: 'DIVISION' }))} data-testid="type-division-btn">
                <Building2 className="h-4 w-4 mr-1" /> {t('business.division')}
              </Button>
            </div>
            <div>
              <Label>{t('business.nameRequired')}</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g., Summer Gala 2026" data-testid="sub-account-name" />
            </div>
            <div>
              <Label>{t('business.description')}</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional description" data-testid="sub-account-description" />
            </div>
            <div>
              <Label>{t('business.defaultMessage')}</Label>
              <Input value={form.default_message} onChange={e => setForm(f => ({ ...f, default_message: e.target.value }))}
                placeholder="e.g., Donation to Summer Gala" data-testid="sub-account-default-message" />
              <p className="text-xs text-muted-foreground mt-1">Pre-filled in payer's message box when paying to this sub-account</p>
            </div>
            <GuestDetailsSwitch checked={form.require_guest_details} testId="sub-account-require-guest-details"
              label={t('business.requireGuestDetails')} hint={t('business.requireGuestDetailsHint')}
              onChange={v => setForm(f => ({ ...f, require_guest_details: v }))} />
            {/* Swish Account Dropdown — only show if more than 1 account */}
            {swishAccounts.length > 1 && (
              <div>
                <Label>{t('auth.selectSwishAccount')}</Label>
                <Select value={form.swish_account_id} onValueChange={v => setForm(f => ({ ...f, swish_account_id: v }))}>
                  <SelectTrigger data-testid="create-swish-select">
                    <SelectValue placeholder="Select Swish account..." />
                  </SelectTrigger>
                  <SelectContent>
                    {swishAccounts.map(sa => (
                      <SelectItem key={sa.id} value={sa.id}>
                        {sa.merchant_number} — {sa.display_name} {sa.is_primary ? '(Primary)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Payments to this sub-account will route to this Swish number</p>
              </div>
            )}
            {/* Accounting Code */}
            <div>
              <Label>{t('auth.accountingCode')}</Label>
              <Input value={form.accounting_code} onChange={e => setForm(f => ({ ...f, accounting_code: e.target.value }))}
                placeholder={t('auth.accountingCodePlaceholder')} data-testid="sub-account-accounting-code" />
              <p className="text-xs text-muted-foreground mt-1">Optional — link to your external accounting system</p>
            </div>
            {/* Payment Mode: Amount or Custom Fields */}
            <div className="space-y-3">
              <Label>{t('business.paymentMode')}</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="create-payment-mode" checked={form.payment_mode === 'AMOUNT'} onChange={() => setForm(f => ({ ...f, payment_mode: 'AMOUNT', line_items: [] }))} className="accent-teal-600" data-testid="radio-amount" />
                  <span className="text-sm">{t('business.amountMode')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="create-payment-mode" checked={form.payment_mode === 'LINE_ITEMS'} onChange={() => setForm(f => ({ ...f, payment_mode: 'LINE_ITEMS', fixed_amount: '' }))} className="accent-teal-600" data-testid="radio-line-items" />
                  <span className="text-sm">{t('business.lineItemsMode')}</span>
                </label>
              </div>

              {form.payment_mode === 'AMOUNT' && (
                <div>
                  <Input type="number" min="0" step="0.01" value={form.fixed_amount} onChange={e => setForm(f => ({ ...f, fixed_amount: e.target.value }))}
                    placeholder={t('business.leaveEmptyCustom')} data-testid="sub-account-fixed-amount" />
                  <p className="text-xs text-muted-foreground mt-1">{t('guestPayment.fixedAmountHint')}</p>
                </div>
              )}

              {form.payment_mode === 'LINE_ITEMS' && (
                <div className="space-y-2">
                  {(form.line_items || []).map((li, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <Input className="flex-1" placeholder={t('business.itemDescription')} value={li.description}
                        onChange={e => { const items = [...form.line_items]; items[idx] = { ...items[idx], description: e.target.value }; setForm(f => ({ ...f, line_items: items })); }}
                        data-testid={`line-item-desc-${idx}`} />
                      <Input className="w-28" type="number" min="0" step="0.01" placeholder={t('business.itemAmount')} value={li.amount}
                        onChange={e => { const items = [...form.line_items]; items[idx] = { ...items[idx], amount: parseFloat(e.target.value) || 0 }; setForm(f => ({ ...f, line_items: items })); }}
                        data-testid={`line-item-amount-${idx}`} />
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => { const items = form.line_items.filter((_, i) => i !== idx); setForm(f => ({ ...f, line_items: items })); }}>
                        <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                      </Button>
                    </div>
                  ))}
                  {(form.line_items || []).length < 4 && (
                    <Button variant="outline" size="sm" onClick={() => setForm(f => ({ ...f, line_items: [...(f.line_items || []), { description: '', amount: 0 }] }))} data-testid="add-line-item-btn">
                      <Plus className="h-3.5 w-3.5 mr-1" /> {t('business.addItem')}
                    </Button>
                  )}
                  {(form.line_items || []).length >= 4 && <p className="text-xs text-muted-foreground">{t('business.maxItemsReached')}</p>}
                </div>
              )}
            </div>
            {form.type === 'EVENT' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('business.startDate')}</Label>
                  <Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} data-testid="sub-account-start-date" />
                </div>
                <div>
                  <Label>{t('business.endDate')}</Label>
                  <Input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} data-testid="sub-account-end-date" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleCreate} data-testid="submit-sub-account">{t('common.submit')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editOpen} onOpenChange={() => setEditOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('common.edit')}: {editOpen?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>{t('business.name')}</Label><Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} data-testid="edit-name" /></div>
            <div><Label>{t('business.description')}</Label><Textarea value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} data-testid="edit-description" /></div>
            <div>
              <Label>Default Message</Label>
              <Input value={editForm.default_message} onChange={e => setEditForm(f => ({ ...f, default_message: e.target.value }))} data-testid="edit-default-message" />
            </div>
            <GuestDetailsSwitch checked={editForm.require_guest_details} testId="edit-require-guest-details"
              label={t('business.requireGuestDetails')} hint={t('business.requireGuestDetailsHint')}
              onChange={v => setEditForm(f => ({ ...f, require_guest_details: v }))} />
            {/* Swish Account Dropdown — only show if more than 1 account */}
            {swishAccounts.length > 1 && (
              <div>
                <Label>{t('auth.selectSwishAccount')}</Label>
                <Select value={editForm.swish_account_id} onValueChange={v => setEditForm(f => ({ ...f, swish_account_id: v }))}>
                  <SelectTrigger data-testid="edit-swish-select">
                    <SelectValue placeholder="Select Swish account..." />
                  </SelectTrigger>
                  <SelectContent>
                    {swishAccounts.map(sa => (
                      <SelectItem key={sa.id} value={sa.id}>
                        {sa.merchant_number} — {sa.display_name} {sa.is_primary ? '(Primary)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Accounting Code */}
            <div>
              <Label>{t('auth.accountingCode')}</Label>
              <Input value={editForm.accounting_code} onChange={e => setEditForm(f => ({ ...f, accounting_code: e.target.value }))}
                placeholder={t('auth.accountingCodePlaceholder')} data-testid="edit-accounting-code" />
            </div>
            {/* Payment Mode: Amount or Custom Fields */}
            <div className="space-y-3">
              <Label>{t('business.paymentMode')}</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="edit-payment-mode" checked={editForm.payment_mode === 'AMOUNT'} onChange={() => setEditForm(f => ({ ...f, payment_mode: 'AMOUNT', line_items: [] }))} className="accent-teal-600" data-testid="edit-radio-amount" />
                  <span className="text-sm">{t('business.amountMode')}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="edit-payment-mode" checked={editForm.payment_mode === 'LINE_ITEMS'} onChange={() => setEditForm(f => ({ ...f, payment_mode: 'LINE_ITEMS', fixed_amount: '' }))} className="accent-teal-600" data-testid="edit-radio-line-items" />
                  <span className="text-sm">{t('business.lineItemsMode')}</span>
                </label>
              </div>

              {editForm.payment_mode === 'AMOUNT' && (
                <div>
                  <Input type="number" min="0" step="0.01" value={editForm.fixed_amount} onChange={e => setEditForm(f => ({ ...f, fixed_amount: e.target.value }))}
                    placeholder={t('business.leaveEmptyCustom')} data-testid="edit-fixed-amount" />
                </div>
              )}

              {editForm.payment_mode === 'LINE_ITEMS' && (
                <div className="space-y-2">
                  {(editForm.line_items || []).map((li, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <Input className="flex-1" placeholder={t('business.itemDescription')} value={li.description}
                        onChange={e => { const items = [...editForm.line_items]; items[idx] = { ...items[idx], description: e.target.value }; setEditForm(f => ({ ...f, line_items: items })); }}
                        data-testid={`edit-line-desc-${idx}`} />
                      <Input className="w-28" type="number" min="0" step="0.01" placeholder={t('business.itemAmount')} value={li.amount}
                        onChange={e => { const items = [...editForm.line_items]; items[idx] = { ...items[idx], amount: parseFloat(e.target.value) || 0 }; setEditForm(f => ({ ...f, line_items: items })); }}
                        data-testid={`edit-line-amount-${idx}`} />
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => { const items = editForm.line_items.filter((_, i) => i !== idx); setEditForm(f => ({ ...f, line_items: items })); }}>
                        <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                      </Button>
                    </div>
                  ))}
                  {(editForm.line_items || []).length < 4 && (
                    <Button variant="outline" size="sm" onClick={() => setEditForm(f => ({ ...f, line_items: [...(f.line_items || []), { description: '', amount: 0 }] }))} data-testid="edit-add-line-item-btn">
                      <Plus className="h-3.5 w-3.5 mr-1" /> {t('business.addItem')}
                    </Button>
                  )}
                </div>
              )}
            </div>
            {editOpen?.type === 'EVENT' && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Start</Label><Input type="date" value={editForm.start_date} onChange={e => setEditForm(f => ({ ...f, start_date: e.target.value }))} /></div>
                <div><Label>End</Label><Input type="date" value={editForm.end_date} onChange={e => setEditForm(f => ({ ...f, end_date: e.target.value }))} /></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(null)}>{t('common.cancel')}</Button>
            <Button onClick={handleEdit} data-testid="save-edit-btn">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* QR Code Dialog - enhanced with business info */}
      <Dialog open={!!qrOpen} onOpenChange={() => setQrOpen(null)}>
        <DialogContent className="text-center">
          <DialogHeader>
            <DialogTitle>{qrOpen?.name} — QR Code</DialogTitle>
            <DialogDescription>{t('business.qrShareDesc')}</DialogDescription>
          </DialogHeader>
          {qrOpen && (
            <div className="flex flex-col items-center gap-3 py-4">
              <p className="text-sm font-medium text-muted-foreground">{businessProfile?.company_name || user?.full_name || 'Business'}</p>
              <QRCodeSVG
                value={qrOpen.payment_url || `${window.location.origin}/pay/${qrOpen.paymig_id}`}
                size={200} level="M" includeMargin
              />
              <div className="text-lg font-mono font-bold">{qrOpen.display_paymig_id || qrOpen.paymig_id}</div>
              {qrOpen.connected_swish_id && <div className="text-sm text-muted-foreground">Swish: {qrOpen.connected_swish_id}</div>}
              <a href="https://paymig.app" target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline">paymig.app</a>
              <div className="flex gap-2 mt-2">
                <Button size="sm" onClick={() => copyPaymigId(qrOpen.paymig_id)} data-testid="copy-paymig-id"><Copy className="h-4 w-4 mr-1" /> Copy ID</Button>
                <Button size="sm" variant="outline" onClick={() => shareQR(qrOpen)} data-testid="share-qr"><Share2 className="h-4 w-4 mr-1" /> Share</Button>
                <Button size="sm" variant="outline" onClick={() => downloadFlyer(qrOpen)} data-testid="download-flyer"><Download className="h-4 w-4 mr-1" /> Flyer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-rose-600">Delete Sub-Account</DialogTitle>
            <DialogDescription>{t('business.deleteConfirm')} <strong>"{deleteConfirm?.name}"</strong>?</DialogDescription>
          </DialogHeader>
          <div className="py-2"><div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800"><strong>Warning:</strong> All existing transactions will be relocated to the root business account.</div></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm.id, deleteConfirm.name)} data-testid="confirm-delete-sub">{t('common.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={!!inviteOpen} onOpenChange={() => { setInviteOpen(null); setInviteSwish(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('business.inviteModerator')}</DialogTitle>
            <DialogDescription>{t('business.swishIdInvite')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>{t('business.swishId')}</Label><Input value={inviteSwish} onChange={e => setInviteSwish(e.target.value)} placeholder="0701234567" data-testid="invite-swish-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setInviteOpen(null); setInviteSwish(''); }}>{t('common.cancel')}</Button>
            <Button onClick={() => handleInvite(inviteOpen)} data-testid="send-invite-btn">{t('business.sendInvitation')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={!!reportOpen} onOpenChange={() => { setReportOpen(null); setReportData(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle><FileText className="h-4 w-4 inline mr-1" /> {t('business.report')}: {reportOpen?.name}</DialogTitle>
            <DialogDescription>{reportOpen?.type} — {reportOpen?.display_paymig_id || reportOpen?.paymig_id}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Date range */}
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <Label>{t('business.from')}</Label>
                <Input type="date" value={reportDateFrom} onChange={e => setReportDateFrom(e.target.value)} data-testid="report-date-from" />
              </div>
              <div className="flex-1">
                <Label>{t('business.to')}</Label>
                <Input type="date" value={reportDateTo} onChange={e => setReportDateTo(e.target.value)} data-testid="report-date-to" />
              </div>
              <Button onClick={loadReport} disabled={reportLoading} data-testid="generate-report-btn">
                {reportLoading ? t('common.loading') : t('business.generateReport')}
              </Button>
            </div>

            {/* Report data */}
            {reportData && (
              <div className="space-y-3">
                <div className="flex gap-4 p-3 bg-teal-50 rounded-lg text-sm">
                  <span><strong className="text-teal-700">{reportData.transaction_count}</strong> {t('business.transactionCount').toLowerCase()}</span>
                  <span className="text-emerald-700">{t('business.from')}: <strong>{(reportData.total_received || 0).toLocaleString()} SEK</strong></span>
                  <span className="text-rose-700">{t('business.to')}: <strong>{(reportData.total_sent || 0).toLocaleString()} SEK</strong></span>
                  <span className="font-semibold text-blue-700">Balance: <strong>{(reportData.balance ?? 0).toLocaleString()} SEK</strong></span>
                </div>

                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="text-left px-2 py-2 font-medium">{t('history.date')}</th>
                        <th className="text-left px-2 py-2 font-medium">{t('business.name')}</th>
                        <th className="text-left px-2 py-2 font-medium">{t('business.swishMessage')}</th>
                        <th className="text-left px-2 py-2 font-medium">{t('business.subAccounts')}</th>
                        <th className="text-right px-2 py-2 font-medium">{t('business.amount')}</th>
                        <th className="text-left px-2 py-2 font-medium">{t('history.type')}</th>
                        {/* Dynamic line item columns */}
                        {(() => {
                          const descs = new Set();
                          (reportData.rows || []).forEach(r => { if (r.line_items_breakdown) r.line_items_breakdown.forEach(li => descs.add(li.description)); });
                          return [...descs].map(d => <th key={d} className="text-center px-2 py-2 font-medium text-violet-600">{d}</th>);
                        })()}
                      </tr>
                    </thead>
                    <tbody>
                      {(reportData.rows || []).map((row, i) => {
                        const allDescs = new Set();
                        (reportData.rows || []).forEach(r => { if (r.line_items_breakdown) r.line_items_breakdown.forEach(li => allDescs.add(li.description)); });
                        return (
                          <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                            <td className="px-2 py-1.5 whitespace-nowrap">{row.date ? new Date(row.date).toLocaleDateString('sv-SE', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</td>
                            <td className="px-2 py-1.5">
                              {row.from_name}
                              {row.is_guest && row.guest_name && <span className="block text-[10px] text-violet-600">Guest: {row.guest_name}</span>}
                            </td>
                            <td className="px-2 py-1.5 text-muted-foreground">{row.description || ''}</td>
                            <td className="px-2 py-1.5">{row.sub_account_name || ''}</td>
                            <td className="px-2 py-1.5 text-right font-semibold">{row.amount} SEK</td>
                            <td className="px-2 py-1.5"><Badge variant="outline" className="text-[10px]">{row.type}</Badge></td>
                            {[...allDescs].map(desc => {
                              const li = (row.line_items_breakdown || []).find(x => x.description === desc);
                              return <td key={desc} className="px-2 py-1.5 text-center">{li ? li.quantity : ''}</td>;
                            })}
                          </tr>
                        );
                      })}
                      {(!reportData.rows || reportData.rows.length === 0) && (
                        <tr><td colSpan={99} className="px-2 py-4 text-center text-muted-foreground">{t('business.noTransactions')}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            {reportData && (
              <Button variant="outline" onClick={downloadReportPdf} data-testid="download-pdf-btn">
                <Download className="h-4 w-4 mr-1" /> {t('business.downloadPdf')}
              </Button>
            )}
            <Button variant="outline" onClick={() => { setReportOpen(null); setReportData(null); }}>{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
